/**
 * Pure operations on the workspace tree.
 *
 * Split out of workspaceStore so they can be exercised without pulling in
 * zustand's persist middleware (which touches localStorage at import time).
 * Nothing here reads or writes anything outside its arguments.
 */
import {
  type PanelNode, type SplitNode, type ViewInstance, type WorkspaceNode,
  isPanelNode,
} from './types'

export function findPanel(node: WorkspaceNode, panelId: string | null): PanelNode | null {
  if (!panelId) return null
  if (isPanelNode(node)) return node.id === panelId ? node : null
  for (const child of node.children) {
    const r = findPanel(child, panelId)
    if (r) return r
  }
  return null
}

export function findParent(
  node: WorkspaceNode, panelId: string,
  parent: SplitNode | null = null,
): SplitNode | null {
  if (isPanelNode(node)) return node.id === panelId ? parent : null
  for (const child of node.children) {
    const r = findParent(child, panelId, node)
    if (r) return r
  }
  return null
}

export function replaceNode(
  node: WorkspaceNode, oldId: string, newNode: WorkspaceNode,
): WorkspaceNode {
  if (node.id === oldId) return newNode
  if (isPanelNode(node)) return node
  return {
    ...node,
    children: [
      replaceNode(node.children[0], oldId, newNode),
      replaceNode(node.children[1], oldId, newNode),
    ] as [WorkspaceNode, WorkspaceNode],
  }
}

export function getAllPanels(node: WorkspaceNode): PanelNode[] {
  if (isPanelNode(node)) return [node]
  return [...getAllPanels(node.children[0]), ...getAllPanels(node.children[1])]
}

/** Every view instance in the tree, in panel order. */
export function getAllViews(node: WorkspaceNode): ViewInstance[] {
  return getAllPanels(node).flatMap((p) => p.views)
}

/** Locate a view instance anywhere in the tree, with its owning panel. */
export function locateView(
  node: WorkspaceNode, instanceId: string | null,
): { panel: PanelNode; view: ViewInstance } | null {
  if (!instanceId) return null
  if (isPanelNode(node)) {
    const view = node.views.find((v) => v.instanceId === instanceId)
    return view ? { panel: node, view } : null
  }
  for (const child of node.children) {
    const r = locateView(child, instanceId)
    if (r) return r
  }
  return null
}

/**
 * The `viewState` bag for an instance, or undefined.
 *
 * Reference-stable between writes — `useViewState` subscribes to this, so an
 * unrelated store update (focus, split resize) must not produce a new object.
 */
export function findViewState(
  node: WorkspaceNode, instanceId: string | null,
): Record<string, unknown> | undefined {
  return locateView(node, instanceId)?.view.viewState
}

/** Shallow equality for a view instance's params bag (undefined === empty). */
export function sameParams(
  a?: Record<string, string>, b?: Record<string, string>,
): boolean {
  const ak = a ? Object.keys(a) : []
  const bk = b ? Object.keys(b) : []
  if (ak.length !== bk.length) return false
  return ak.every((k) => a![k] === b?.[k])
}

/**
 * Marks a `viewState` key as session-only: kept across tab switches (which
 * unmount the view) but dropped before the tree is persisted, so it does not
 * outlive a reload. For work-in-progress that shouldn't come back days later —
 * a half-built order, a staged roll.
 */
export const TRANSIENT_PREFIX = '~'

const isTransientKey = (k: string) => k.startsWith(TRANSIENT_PREFIX)

/** A copy of the tree with every transient `viewState` key removed. */
export function stripTransient(node: WorkspaceNode): WorkspaceNode {
  if (!isPanelNode(node)) {
    const a = stripTransient(node.children[0])
    const b = stripTransient(node.children[1])
    // Unchanged subtrees come back as-is, so persisting a tree with nothing
    // transient in it doesn't allocate a copy of the whole thing.
    if (a === node.children[0] && b === node.children[1]) return node
    return { ...node, children: [a, b] as [WorkspaceNode, WorkspaceNode] }
  }
  let touched = false
  const views = node.views.map((v) => {
    if (!v.viewState) return v
    const keys = Object.keys(v.viewState)
    if (!keys.some(isTransientKey)) return v
    touched = true
    const kept: Record<string, unknown> = {}
    for (const k of keys) if (!isTransientKey(k)) kept[k] = v.viewState[k]
    return { ...v, viewState: kept }
  })
  return touched ? { ...node, views } : node
}

/**
 * Resolve one `viewState` write.
 *
 * `next` is a value or an updater; an updater sees the stored value, or
 * `fallback` (the view's initial) when the key has never been written.
 * Returns null when the write changes nothing — no-op writes are common (a
 * page re-setting the same value each poll) and would otherwise churn the tree
 * and the persisted blob on every render.
 */
export function nextViewState(
  bag: Record<string, unknown> | undefined,
  key: string,
  next: unknown,
  fallback: unknown,
): Record<string, unknown> | null {
  const had = !!bag && Object.prototype.hasOwnProperty.call(bag, key)
  const prev = had ? bag![key] : fallback
  const value = typeof next === 'function'
    ? (next as (p: unknown) => unknown)(prev)
    : next
  if (had && Object.is(bag![key], value)) return null
  return { ...(bag ?? {}), [key]: value }
}
